// 画面の読みやすさ3件 (2026-11-04・初見監査 docs/research/2026-11-04-blind-audit.md の「遊び・見た目」)。
//   ゲームを知らない AI 3人の指摘 = ①相手の体力の線が細くて読めない ②会話場面の2択が選べると分からない ③幕の数 (名前の横の玉) を稽古で教えていない。
//   11-04 作者の答え (db answers/blind-audit-1104) で、体力 = a・2択 = a・幕の玉 = b を配布版の既定にした。
//   前の版は開発版の ?bossHp=old・?advChoice=old・?tutActs=old。残りの案 (体力 b・2択 b・幕の玉 a) も開発版の旗で見られる。
//   記録 = docs/research/2026-11-04-blind-audit-readability.md
//
//   ?bossHp=a|b|old  体力の溝の太さ。a = 1.5倍 (約7.7px)・b = 2倍 (約10.3px)。体幹 (約14.4px) より細いまま (10-04 作者「体幹をメインだとわかるように太く」)。
//                    枠の絵は同じ比で大きくし、帯の下が画面に収まるよう名札・体幹ごと上へ寄せる (src/boss_hud_view.ts silverHudLayout)。
//   ?advChoice=a|b|old 2択。a = 一つずつ淡い墨の帯に乗せる (選んでいる方の帯は少し温かい)・b = 形は足さず、選んでいない方を濃くし、案内を2択のすぐ下へ大きく出す。
//                    絵のある場面 (src/adv_picture_view.ts) と絵のない場面 (src/render.ts drawAdvChoice) の両方。
//   ?tutActs=a|b|old 稽古で幕の玉を教える。a = 頭の「下の HUD」の場面の言葉に玉の話を足す・b = 仕上げの致命の場面で玉も照らし、言葉で教える。
import type { FocusStepSpec, FocusTargetId } from './tut_focus'

export type ReadabilityVariant = 'now' | 'a' | 'b'
// 指定なし・知らない値 = 配布版の既定 (def)。old = 11-04 より前の配布版。
const pick = (v: string | null | undefined, def: ReadabilityVariant): ReadabilityVariant => (v === 'old' ? 'now' : v === 'a' || v === 'b' ? v : def)

let bossHp: ReadabilityVariant = 'a'
let advChoice: ReadabilityVariant = 'a'
let tutActs: ReadabilityVariant = 'b'

export function setBossHpLook(v: string | null | undefined): ReadabilityVariant { return (bossHp = pick(v, 'a')) }
export function setAdvChoiceLook(v: string | null | undefined): ReadabilityVariant { return (advChoice = pick(v, 'a')) }
export function bossHpLook(): ReadabilityVariant { return bossHp }
export function advChoiceLook(): ReadabilityVariant { return advChoice }
export function tutActsLook(): ReadabilityVariant { return tutActs }

/** 体力の溝の太さの倍率 (配布版 1)。 */
export function bossHpThickness(): number {
  return bossHp === 'a' ? 1.5 : bossHp === 'b' ? 2 : 1
}

// ---- ③ 幕の玉を稽古で教える ----
//   言葉は .claude/skills/shinige-world-japanese と声の口調 (src/tut_voice.ts の頭) に合わせる。数は算用数字。
//   声は1行で折り返さないので、36字まで (11-04 撮影で 46字以上は画面の端からはみ出した)。
export const TUT_ACTS_LINES = {
  a: {
    hud_boss: { card: '下が、相手の体力と体幹。名前の横の玉が、幕の数', voice: '下にあるのは、相手の体力と体幹。名前の横の玉は、相手の幕の数' },
  },
  b: {
    finale_execute: { card: '崩れた相手へ、致命の一撃。名前の横の玉が1つ消える', voice: '崩れたね。致命の一撃をあげて。名前の横の玉が1つ消えたら、幕が閉じた合図' },
  },
} as const

/** 名前の横の玉の、いま描いている位置 (render.ts drawActMarks が毎コマ書く)。照らしの穴に使う。 */
export const actMarksAt = { x: 0, y: 0, w: 0, ok: false }

/** ?tutActs の値を、止める場面の表と声の台本へ当てる (applyPostureHoldLook と同じ形)。配布版は null で呼び、既定 b を当てる。1回だけ呼ぶ。 */
export function applyTutActs(
  v: string | null | undefined,
  steps: Record<'hud_boss' | 'finale_execute', FocusStepSpec>,
  voice: Record<'hud_boss' | 'finale_execute', { line: string; hint?: string }>,
): ReadabilityVariant {
  tutActs = pick(v, 'b')
  if (tutActs === 'a') {
    const L = TUT_ACTS_LINES.a.hud_boss
    steps.hud_boss = { ...steps.hud_boss, line: L.card }
    voice.hud_boss = { ...voice.hud_boss, line: L.voice }
  } else if (tutActs === 'b') {
    const L = TUT_ACTS_LINES.b.finale_execute
    const targets: FocusTargetId[] = ['boss_body', 'hud_boss_acts']
    steps.finale_execute = { ...steps.finale_execute, targets, line: L.card }
    voice.finale_execute = { ...voice.finale_execute, line: L.voice }
  }
  return tutActs
}

// ---- ② 会話場面の2択 ----
/** 選んでいない方の濃さ (配布版 0.5)。 */
export function advChoiceIdleAlpha(): number {
  return advChoice === 'now' ? 0.5 : 0.85
}
/** 選んでいない方の色 (配布版 #b9a3d9)。 */
export function advChoiceIdleColor(): string {
  return advChoice === 'now' ? '#b9a3d9' : '#d2c2ee'
}

/** 案 a: 選択肢1つ分の淡い墨の帯。縁を作らず、真ん中から四方へ薄れる (箱にも線にも見せない)。選んでいる方は少し温かい。 */
export function drawChoiceInk(ctx: CanvasRenderingContext2D, cx: number, cy: number, w: number, h: number, selected: boolean, alpha: number): void {
  if (advChoice !== 'a' || alpha <= 0) return
  ctx.save()
  ctx.globalAlpha = alpha
  ctx.translate(cx, cy)
  ctx.scale(w / 2, h / 2)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 1)
  const c = selected ? '58,40,18' : '10,6,20'
  const a = selected ? 0.78 : 0.72
  g.addColorStop(0, `rgba(${c},${a})`)
  g.addColorStop(0.55, `rgba(${c},${(a * 0.82).toFixed(3)})`)
  g.addColorStop(1, `rgba(${c},0)`)
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(0, 0, 1, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}
